import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Search, Star, AlertTriangle, XCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "active", label: "Active" },
  { value: "vacation", label: "Vacation" },
  { value: "on_hold", label: "On Hold" },
  { value: "suspended", label: "Suspended" },
  { value: "archived", label: "Archived" },
];

const statusColor = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  available: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  vacation: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  on_hold: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  suspended: "bg-red-500/15 text-red-400 border-red-500/30",
  archived: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  busy: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  off_duty: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const EMPTY_FORM = {
  name: "", driver_number: "", phone: "", email: "", address: "",
  ssn: "", birth_date: "", start_date: "", status: "active", notes: "",
  license_number: "", license_expiry: "", tlc_license_number: "",
  tlc_license_expiry: "", ddc_last_completed: "",
  base_dues_balance: 0, billing_type: "", commission_rate: 0, flat_fee_amount: 0,
  billing_exempt: false,
  default_vehicle_id: "",
};

function maskSSN(ssn) {
  if (!ssn) return "";
  const digits = ssn.replace(/\D/g, "");
  if (digits.length < 4) return ssn;
  return `***-**-${digits.slice(-4)}`;
}

function getDocStatus(expiryDate) {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate + "T00:00:00");
  if (isNaN(expiry.getTime())) return null;
  const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "expired";
  if (diffDays <= 30) return "expiring";
  return "ok";
}

function getDriverDocStatus(driver) {
  const dates = [
    driver.license_expiry,
    driver.tlc_license_expiry,
  ];
  let worst = "ok";
  for (const d of dates) {
    const s = getDocStatus(d);
    if (s === "expired") return "expired";
    if (s === "expiring") worst = "expiring";
  }
  // If no dates set at all, treat as neutral
  if (dates.every((d) => !d)) return null;
  return worst;
}

function formatBirthday(dateStr) {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.split("-");
  return `${m}/${d}/${y}`;
}

function VehicleCard({ vehicle }) {
  if (!vehicle) return null;
  const regStatus = getDocStatus(vehicle.registration_expiry);
  const insStatus = getDocStatus(vehicle.insurance_expiry);
  const hasWarning = regStatus === "expiring" || insStatus === "expiring";
  const hasExpired = regStatus === "expired" || insStatus === "expired";

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4 space-y-3" data-testid="vehicle-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Default Vehicle</span>
        </div>
        <div className="flex items-center gap-1.5">
          {hasExpired && <XCircle className="w-4 h-4 text-red-400" />}
          {hasWarning && !hasExpired && <AlertTriangle className="w-4 h-4 text-amber-400" />}
        </div>
      </div>
      <div className="text-lg font-medium text-foreground">
        {vehicle.make} {vehicle.model} {vehicle.year && <span className="text-muted-foreground text-sm">({vehicle.year})</span>}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Plate</span>
          <div className="font-medium" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{vehicle.plate_number}</div>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Type</span>
          <div>{(vehicle.vehicle_type || "").toUpperCase() === "SUV" ? "SUV" : (vehicle.vehicle_type || "—").charAt(0).toUpperCase() + (vehicle.vehicle_type || "—").slice(1)}</div>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Registration</span>
          <div className={`flex items-center gap-1 ${regStatus === "expired" ? "text-red-400" : regStatus === "expiring" ? "text-amber-400" : ""}`}>
            {vehicle.registration_expiry || "—"}
            {regStatus === "expired" && <XCircle className="w-3 h-3" />}
            {regStatus === "expiring" && <AlertTriangle className="w-3 h-3" />}
          </div>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Insurance</span>
          <div className={`flex items-center gap-1 ${insStatus === "expired" ? "text-red-400" : insStatus === "expiring" ? "text-amber-400" : ""}`}>
            {vehicle.insurance_expiry || "—"}
            {insStatus === "expired" && <XCircle className="w-3 h-3" />}
            {insStatus === "expiring" && <AlertTriangle className="w-3 h-3" />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [showSSN, setShowSSN] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [tlcLookingUp, setTlcLookingUp] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { loadData(); }, []);

  // Auto-open edit dialog when ?open=<id> is present (e.g., from Licensing page)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || drivers.length === 0) return;
    const driver = drivers.find((d) => d.id === openId);
    if (driver) {
      openEdit(driver);
      // clean the URL
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers]);

  const loadData = async () => {
    try {
      const [d, v] = await Promise.all([api.get("/drivers"), api.get("/vehicles")]);
      setDrivers(d.data);
      setVehicles(v.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingDriver(null);
    setForm({ ...EMPTY_FORM });
    setShowSSN(false);
    setActiveTab("personal");
    setShowDialog(true);
  };

  const openEdit = (driver) => {
    setEditingDriver(driver);
    setForm({
      name: driver.name || "",
      driver_number: driver.driver_number || "",
      phone: driver.phone || "",
      email: driver.email || "",
      address: driver.address || "",
      ssn: driver.ssn || "",
      birth_date: driver.birth_date || "",
      start_date: driver.start_date || "",
      status: driver.status || "active",
      notes: driver.notes || "",
      license_number: driver.license_number || "",
      license_expiry: driver.license_expiry || "",
      tlc_license_number: driver.tlc_license_number || "",
      tlc_license_expiry: driver.tlc_license_expiry || "",
      ddc_last_completed: driver.ddc_last_completed || "",
      base_dues_balance: driver.base_dues_balance || 0,
      billing_type: driver.billing_type || "",
      commission_rate: driver.commission_rate || 0,
      flat_fee_amount: driver.flat_fee_amount || 0,
      billing_exempt: driver.billing_exempt || false,
      default_vehicle_id: driver.default_vehicle_id || driver.vehicle_id || "",
      tlc_driver_last_date_updated: driver.tlc_driver_last_date_updated || "",
      tlc_driver_last_time_updated: driver.tlc_driver_last_time_updated || "",
    });
    setShowSSN(false);
    setActiveTab("personal");
    setShowDialog(true);
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_dues_balance: parseFloat(form.base_dues_balance) || 0,
        commission_rate: parseFloat(form.commission_rate) || 0,
        flat_fee_amount: parseFloat(form.flat_fee_amount) || 0,
        vehicle_id: form.default_vehicle_id || null,
        default_vehicle_id: form.default_vehicle_id || null,
      };
      if (editingDriver) {
        await api.put(`/drivers/${editingDriver.id}`, payload);
        toast.success("Driver updated");
      } else {
        await api.post("/drivers", payload);
        toast.success("Driver added");
      }
      setShowDialog(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this driver?")) return;
    try {
      await api.delete(`/drivers/${id}`);
      toast.success("Driver deleted");
      loadData();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleSendAppInvite = async (d) => {
    if (!d.email) { toast.error("Driver has no email on file"); return; }
    if (!window.confirm(`Send driver app invite to ${d.email}?\n\nThis will email a temporary password. If they've been invited before, a fresh password will be issued and prior sessions revoked.`)) return;
    try {
      const { data } = await api.post(`/drivers/${d.id}/send-app-invite`);
      if (data.email_sent) {
        toast.success(`Invite sent to ${data.email}`);
      } else {
        toast.warning(`Invite created but email delivery failed. Check Resend config.`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send invite"); }
  };

  const handleDisableAppAccess = async (d) => {
    if (!window.confirm(`Disable driver app access for ${d.name}?\n\nAll active sessions will be revoked immediately. They can be re-enabled later.`)) return;
    try {
      await api.post(`/drivers/${d.id}/disable-app-access`);
      toast.success("App access disabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to disable access"); }
  };

  const handleEnableAppAccess = async (d) => {
    try {
      await api.post(`/drivers/${d.id}/enable-app-access`);
      toast.success("App access enabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to enable access"); }
  };

  const tlcDriverLookup = async () => {
    const tlc = form.tlc_license_number?.trim();
    if (!tlc) { toast.error("Enter a TLC License # first"); return; }
    setTlcLookingUp(true);
    try {
      const { data } = await api.get(`/drivers/tlc-driver-lookup?tlc=${encodeURIComponent(tlc)}`);
      setForm((prev) => ({
        ...prev,
        name: data.tlc_name || prev.name,
        tlc_license_expiry: data.tlc_license_expiry || prev.tlc_license_expiry,
        tlc_driver_last_date_updated: data.tlc_driver_last_date_updated || "",
        tlc_driver_last_time_updated: data.tlc_driver_last_time_updated || "",
      }));
      toast.success("TLC driver data loaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "TLC driver lookup failed");
    } finally { setTlcLookingUp(false); }
  };

  const selectedVehicle = useMemo(() => {
    if (!form.default_vehicle_id) return null;
    return vehicles.find((v) => v.id === form.default_vehicle_id) || null;
  }, [form.default_vehicle_id, vehicles]);

  // Filter out archived vehicles but preserve currently-selected archived vehicle (with '(Archived)' label)
  const assignableVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      // Always include the currently selected vehicle (even if archived)
      if (v.id === form.default_vehicle_id) return true;
      // Otherwise, exclude archived vehicles
      return v.status !== "archived";
    });
  }, [vehicles, form.default_vehicle_id]);

  const filtered = drivers.filter((d) => {
    // Filter by archived status
    if (!showArchived && d.status === "archived") return false;
    // Filter by search query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return d.name?.toLowerCase().includes(q) || d.phone?.includes(q) || d.email?.toLowerCase().includes(q) || d.driver_number?.includes(q);
    }
    return true;
  });

  return (
    <div data-testid="drivers-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>Drivers</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your fleet drivers</p>
        </div>
        <Button onClick={openCreate} data-testid="add-driver-button">
          <Plus className="w-4 h-4 mr-1" /> Add Driver
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search drivers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-card border-border" data-testid="drivers-search" />
        </div>
        <div className="flex items-center gap-2" data-testid="drivers-show-archived-toggle">
          <Switch
            checked={showArchived}
            onCheckedChange={setShowArchived}
            data-testid="drivers-show-archived-switch"
          />
          <span className="text-xs text-muted-foreground">Show archived</span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-4" data-testid="drivers-doc-legend">
          <div className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/80" />
            <span className="text-xs text-muted-foreground">Expired</span>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-amber-400/80" />
            <span className="text-xs text-muted-foreground">Expires 30 days</span>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-emerald-400/80" />
            <span className="text-xs text-muted-foreground">Current</span>
          </div>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead>Driver #</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="w-[80px] text-center">TLC Lic Status</TableHead>
              <TableHead className="w-[50px] text-center">Docs</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No drivers found</TableCell></TableRow>
            ) : (
              filtered.map((d) => {
                const docStatus = getDriverDocStatus(d);
                return (
                <TableRow
                  key={d.id}
                  className="cursor-pointer hover:bg-muted/20 transition-colors"
                  onClick={() => openEdit(d)}
                  data-testid={`driver-row-${d.id}`}
                >
                  <TableCell className="font-semibold" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{d.driver_number || "—"}</TableCell>
                  <TableCell className="font-medium">{d.name}</TableCell>
                  <TableCell className="text-sm">{d.phone || "—"}</TableCell>
                  <TableCell className="text-sm">{d.email || "—"}</TableCell>
                  <TableCell className="text-center">
                    {d.tlc_status === "active" && <span className="inline-block w-3 h-3 rounded-full bg-emerald-400/80" title="TLC Active" data-testid={`driver-tlc-status-${d.id}`} />}
                    {d.tlc_status === "suspended" && <span className="inline-block w-3 h-3 rounded-full bg-red-500/80" title="TLC Suspended" data-testid={`driver-tlc-status-${d.id}`} />}
                    {!d.tlc_status && <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {docStatus === "expired" && <span className="inline-block w-3 h-3 rounded-full bg-red-500/80" title="Document expired" data-testid={`driver-doc-${d.id}`} />}
                    {docStatus === "expiring" && <span className="inline-block w-3 h-3 rounded-full bg-amber-400/80" title="Document expiring soon" data-testid={`driver-doc-${d.id}`} />}
                    {docStatus === "ok" && <span className="inline-block w-3 h-3 rounded-full bg-emerald-400/80" title="Documents current" data-testid={`driver-doc-${d.id}`} />}
                    {docStatus === null && <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${statusColor[d.status] || statusColor.active}`}>{(d.status || "active").replace("_", " ")}</Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`driver-actions-${d.id}`}><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-card border-border">
                        <DropdownMenuItem onClick={() => openEdit(d)}>Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/billing?tab=ledger&driver=${d.id}`)} data-testid={`driver-ledger-${d.id}`}>View Ledger</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleSendAppInvite(d)} data-testid={`driver-send-app-invite-${d.id}`} disabled={!d.email}>Send App Invite</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDisableAppAccess(d)} data-testid={`driver-disable-app-${d.id}`}>Disable App Access</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEnableAppAccess(d)} data-testid={`driver-enable-app-${d.id}`}>Enable App Access</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(d.id)} className="text-red-400">Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* ========== TABBED DRIVER DIALOG ========== */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="driver-dialog">
          <DialogHeader className="px-6 pt-5 pb-0 pr-12">
            <DialogTitle style={{ fontFamily: "'Manrope', sans-serif" }}>
              {editingDriver ? "Edit Driver" : "Add Driver"}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="px-6 pt-3">
            <TabsList className="w-full grid grid-cols-4" data-testid="driver-tabs">
              <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
              <TabsTrigger value="licensing" data-testid="tab-licensing">Licensing</TabsTrigger>
              <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
              <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles</TabsTrigger>
            </TabsList>

            {/* ====== PERSONAL TAB ====== */}
            <TabsContent value="personal" className="space-y-4 py-4" data-testid="tab-personal-content">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Name *</Label>
                  <Input value={form.name} onChange={(e) => updateField("name", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Driver #</Label>
                  <Input value={form.driver_number} onChange={(e) => updateField("driver_number", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-number-input" placeholder="e.g. 14" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <Input value={form.phone} onChange={(e) => updateField("phone", e.target.value)} placeholder="+1..." className="bg-muted/20 border-border" data-testid="driver-phone-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input value={form.email} onChange={(e) => updateField("email", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Address</Label>
                <Input value={form.address} onChange={(e) => updateField("address", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">SS# or EIN</Label>
                  <div className="relative">
                    <Input
                      value={showSSN ? form.ssn : maskSSN(form.ssn)}
                      onChange={(e) => updateField("ssn", e.target.value)}
                      onFocus={() => setShowSSN(true)}
                      className="bg-muted/20 border-border pr-9"
                      data-testid="driver-ssn-input"
                      placeholder="XXX-XX-XXXX"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSSN(!showSSN)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="driver-ssn-toggle"
                    >
                      {showSSN ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Birth Date</Label>
                  <Input type="date" value={form.birth_date} onChange={(e) => updateField("birth_date", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-birth-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => updateField("start_date", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-start-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select value={form.status} onValueChange={(v) => updateField("status", v)}>
                    <SelectTrigger className="bg-muted/20 border-border" data-testid="driver-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* ====== LICENSING TAB ====== */}
            <TabsContent value="licensing" className="space-y-4 py-4" data-testid="tab-licensing-content">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">DMV License #</Label>
                  <Input value={form.license_number} onChange={(e) => updateField("license_number", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-dmv-license" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">DMV Expiration</Label>
                  <Input type="date" value={form.license_expiry} onChange={(e) => updateField("license_expiry", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-dmv-expiry" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">TLC License #</Label>
                  <div className="flex gap-1.5">
                    <Input value={form.tlc_license_number} onChange={(e) => updateField("tlc_license_number", e.target.value)} className="bg-muted/20 border-border flex-1" data-testid="driver-tlc-license" />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={tlcDriverLookup}
                      disabled={tlcLookingUp}
                      className="border-primary/40 text-primary hover:bg-primary/10 shrink-0 h-9 px-3 text-xs"
                      data-testid="driver-tlc-lookup-btn"
                    >
                      {tlcLookingUp ? "..." : "Lookup"}
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">TLC Expiration</Label>
                  <Input type="date" value={form.tlc_license_expiry} onChange={(e) => updateField("tlc_license_expiry", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-tlc-expiry" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Defensive Driving Course (DDC) — Last Completed</Label>
                <Input type="date" value={form.ddc_last_completed} onChange={(e) => updateField("ddc_last_completed", e.target.value)} className="bg-muted/20 border-border" data-testid="driver-ddc-date" />
              </div>
              {/* TLC Last Updated */}
              {(form.tlc_driver_last_date_updated || form.tlc_driver_last_time_updated) && (
                <div className="flex items-center gap-4 pt-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">TLC Last Updated</span>
                  <span className="text-xs text-muted-foreground" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                    {form.tlc_driver_last_date_updated || "—"} {form.tlc_driver_last_time_updated ? `at ${form.tlc_driver_last_time_updated}` : ""}
                  </span>
                </div>
              )}
            </TabsContent>

            {/* ====== BILLING TAB ====== */}
            <TabsContent value="billing" className="space-y-4 py-4" data-testid="tab-billing-content">
              <div className="flex items-center justify-between rounded-md border border-border/50 bg-muted/10 px-3 py-2.5">
                <div>
                  <Label className="text-xs text-foreground">Billing Exempt</Label>
                  <p className="text-[10px] text-muted-foreground/70">Owners/management — skip base dues deductions</p>
                </div>
                <Switch
                  checked={!!form.billing_exempt}
                  onCheckedChange={(v) => updateField("billing_exempt", v)}
                  data-testid="driver-billing-exempt-switch"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Base Dues Balance</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                  <Input
                    type="number" min={0} step={0.01}
                    value={form.base_dues_balance}
                    onChange={(e) => updateField("base_dues_balance", e.target.value)}
                    className="bg-muted/20 border-border pl-7"
                    data-testid="driver-base-dues"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Billing Type</Label>
                <Select value={form.billing_type} onValueChange={(v) => updateField("billing_type", v)}>
                  <SelectTrigger className="bg-muted/20 border-border" data-testid="driver-billing-type">
                    <SelectValue placeholder="Select billing type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commission">Commission</SelectItem>
                    <SelectItem value="flat_fee">Flat Fee per Week</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(form.billing_type === "commission" || form.billing_type === "both") && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Commission Rate</Label>
                  <div className="relative">
                    <Input
                      type="number" min={0} max={100} step={0.5}
                      value={form.commission_rate}
                      onChange={(e) => updateField("commission_rate", e.target.value)}
                      className="bg-muted/20 border-border pr-7"
                      data-testid="driver-commission-rate"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                  </div>
                </div>
              )}
              {(form.billing_type === "flat_fee" || form.billing_type === "both") && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Flat Fee per Week</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                    <Input
                      type="number" min={0} step={0.01}
                      value={form.flat_fee_amount}
                      onChange={(e) => updateField("flat_fee_amount", e.target.value)}
                      className="bg-muted/20 border-border pl-7"
                      data-testid="driver-flat-fee"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ====== VEHICLES TAB ====== */}
            <TabsContent value="vehicles" className="space-y-4 py-4" data-testid="tab-vehicles-content">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Assign Default Vehicle</Label>
                <Select
                  value={form.default_vehicle_id}
                  onValueChange={(v) => updateField("default_vehicle_id", v === "none" ? "" : v)}
                >
                  <SelectTrigger className="bg-muted/20 border-border" data-testid="driver-vehicle-select">
                    <SelectValue placeholder="Select a vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vehicle assigned</SelectItem>
                    {assignableVehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.make} {v.model} {v.year ? `(${v.year})` : ""} — {v.plate_number}{v.status === "archived" ? " (Archived)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedVehicle ? (
                <VehicleCard vehicle={selectedVehicle} />
              ) : (
                <div className="rounded-lg border border-border/50 bg-muted/5 p-8 text-center space-y-2">
                  <Star className="w-6 h-6 text-muted-foreground/20 mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    {vehicles.length === 0
                      ? "No vehicles in system — add vehicles from the Vehicles page"
                      : "Select a vehicle from the dropdown to assign it to this driver"
                    }
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="px-6 py-4 border-t border-border">
            <Button variant="ghost" onClick={() => setShowDialog(false)} data-testid="driver-cancel-button">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-driver-button">
              {saving ? "Saving..." : editingDriver ? "Update Driver" : "Add Driver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
